import { useRouter } from 'expo-router';
import { GraduationCap, X } from 'lucide-react-native';
import { useState } from 'react';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { twJoin } from 'tailwind-merge';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { NewBadge } from '@/components/ui/new-badge';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { DURATION } from '@/constants/animation';
import { GLOBAL_BANNER_CARD, GLOBAL_BANNER_CONTENT } from '@/constants/banners';
import { ROUTES } from '@/constants/routes';
import { useFellowMentorBanner } from '@/hooks/useFellowMentorBanner';
import { useNewBadgePulse } from '@/hooks/useNewBadgePulse';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { trackFellowsBannerClicked, trackFellowsBannerDismissed } from '@/util/analyticsEvents';

/**
 * Global banner promoting the Fellows Corner mentorship experience.
 *
 * One of two entry points to `/fellows`, alongside the web nav link. Both, and
 * the route itself, are gated on the same feature flag, so the promo cannot
 * advertise a page the reader would be redirected away from.
 *
 * Sits beneath a screen's announcement when both are eligible, per the tier
 * order in `banners.md`. It takes the lower position because it is the
 * dismissable one of the pair, so clearing it shortens the stack from the bottom
 * — which is also why the dismiss control is not optional here.
 *
 * Shares the announcement's success field and card geometry, so a stack of the
 * two reads as one system rather than two designs a shade apart, with brand
 * accents carrying the promo identity. Rendered web-only by the secure layout,
 * so web styling alone carries it.
 *
 * TODO: Move banner copy and CTA into a backend-configurable payload, the way
 * the announcement banner already works, so a new promo does not need a
 * frontend deploy. That payload should also carry an audience rule per banner,
 * resolved server-side to a single banner per user, rather than each cohort's
 * banner competing for the slot in the layout.
 */
export function FellowMentorBanner() {
  const router = useRouter();

  const { isShowing, dismiss } = useFellowMentorBanner();

  /**
   * Drops the promo as soon as its fade finishes rather than waiting for the
   * callout round trip, so the announcement takes the slot on the same frame the
   * promo clears. It also means a failed resolve leaves the slot empty for the
   * session instead of restoring a banner the reader already watched leave.
   */
  const [isFadedOut, setIsFadedOut] = useState(false);

  const opacity = useSharedValue(1);

  const prefersReducedMotion = useReducedMotion();

  const fadeStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));

  const badgePulse = useNewBadgePulse();

  const handleDismiss = () => {
    trackFellowsBannerDismissed()?.catch(() => {});

    dismiss();

    if (prefersReducedMotion) {
      setIsFadedOut(true);

      return;
    }

    opacity.value = withTiming(0, { duration: DURATION.fast }, (finished) => {
      if (finished) {
        runOnJS(setIsFadedOut)(true);
      }
    });
  };

  if (!isShowing || isFadedOut) {
    return null;
  }

  return (
    // Reanimated drives opacity here, so the fill and width live on a plain
    // child: an animated view takes styles through `style`, not `className`,
    // and a background class on one does not paint reliably on native either.
    <Animated.View style={fadeStyle}>
      <View className={twJoin('bg-background-success', GLOBAL_BANNER_CARD)}>
        {/* In the card's own corner rather than against the content column: the
            card runs the width of the window while its contents do not, so
            column-relative left the glyph stranded well inside the edge. */}
        <Pressable
          onPress={handleDismiss}
          className="absolute right-2 top-2 z-20 min-h-11 min-w-11 items-center justify-center rounded-full web:cursor-pointer web:transition-colors web:duration-150 web:hover:bg-primary-100"
          {...getAccessibilityProps({
            role: 'button',
            label: 'Dismiss Fellows Corner banner',
          })}>
          <Icon as={X} size="lg" className="text-typography-900 web:cursor-pointer" />
        </Pressable>

        <View className={twJoin('relative py-2', GLOBAL_BANNER_CONTENT)}>
          <HStack className="w-full min-w-0 items-center justify-between gap-4 web:flex-col web:items-start web:gap-3 web:sm:flex-row web:sm:items-center web:sm:pr-12">
            <HStack className="min-w-0 flex-1 items-center gap-4">
              <View
                aria-hidden
                className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 sm:flex">
                <Icon as={GraduationCap} className="h-5 w-5 text-typography-0" />
              </View>

              {/* One flowing line, matching the announcement: bold label, then the
                  sentence. The badge is an element rather than text, so it cannot
                  nest inside the copy the way the announcement's bold title does
                  — it sits between the two as a wrapping flex item instead, which
                  reads the same and still collapses to its own line when narrow. */}
              <HStack className="min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
                <Text className="font-bold font-heading text-base text-typography-900">
                  Fellows Corner
                </Text>

                <Animated.View
                  ref={badgePulse.ref}
                  style={badgePulse.style}
                  className="self-center">
                  <NewBadge surface="card" />
                </Animated.View>

                <Text className="min-w-0 flex-1 text-sm text-typography-700">
                  Teaching you what they don&apos;t teach you in oncology fellowship — career paths,
                  contracting, and the decisions ahead.
                </Text>
              </HStack>
            </HStack>

            <Button
              size="sm"
              className="shrink-0 bg-primary-500 web:w-full data-[hover=true]:bg-primary-600 web:sm:w-auto"
              onPress={() => {
                trackFellowsBannerClicked()?.catch(() => {});

                router.push(ROUTES.FELLOWS);
              }}>
              <ButtonText className="font-normal text-typography-0">Ask a Mentor</ButtonText>
            </Button>
          </HStack>
        </View>
      </View>
    </Animated.View>
  );
}
